import { ThemeEvents } from '@theme/events';
import { bundleStockLabels, getBundleAvailability, renderBundleStockMarkup, unavailableMarkup } from '@theme/bundle-stock';

/**
 * Bundle stock on list pages.
 *
 * A bundle card cannot print its per-location stock from Liquid: there is no
 * location_stock metafield on a bundle variant, the figure is computed by the
 * app proxy (see assets/bundle-stock.js), the same call the product page
 * makes. Each bundle card asks for its own variant, regular cards ask for
 * nothing. Two things keep a page of bundles from stampeding the Lambda:
 * answers are cached per variant for the life of the page, and at most
 * BUNDLE_STOCK_MAX_IN_FLIGHT requests run at once, the rest queue.
 *
 * A batched shape (one request carrying every id on the page, answered by
 * a few grouped Shopify queries) was written and shelved: the per-card calls
 * were judged fast enough for the number of bundles in the catalog. It lives
 * as a stash in jayco-connector should that change.
 */
const BUNDLE_STOCK_MAX_IN_FLIGHT = 4;

/** @type {Map<string, Promise<import('@theme/bundle-stock').BundleAvailability>>} */
const bundleStockCache = new Map();
/** @type {Array<() => void>} */
const bundleStockQueue = [];
let bundleStockInFlight = 0;

/**
 * @param {string} variantId
 * @returns {Promise<import('@theme/bundle-stock').BundleAvailability>}
 */
function requestBundleStock(variantId) {
  const cached = bundleStockCache.get(variantId);
  if (cached) return cached;

  const promise = new Promise((resolve, reject) => {
    const run = () => {
      bundleStockInFlight += 1;
      getBundleAvailability(variantId)
        .then(resolve, reject)
        .finally(() => {
          bundleStockInFlight -= 1;
          bundleStockQueue.shift()?.();
        });
    };

    if (bundleStockInFlight < BUNDLE_STOCK_MAX_IN_FLIGHT) {
      run();
    } else {
      bundleStockQueue.push(run);
    }
  });

  // A failed answer is not kept: the next card, or the next variant change on
  // this card, gets to retry instead of inheriting the error for the page.
  promise.catch(() => bundleStockCache.delete(variantId));
  bundleStockCache.set(variantId, promise);
  return promise;
}

class CustomProductCard extends HTMLElement {
  connectedCallback() {
    // Listen for variant clicks specifically inside this product card
    this.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate.bind(this));

    this.loadBundleStock();
  }

  /** @returns {HTMLElement | null} */
  get bundleStockContainer() {
    if (this.dataset.isBundle !== 'true') return null;
    return this.querySelector('.product-card__location-stock');
  }

  async loadBundleStock() {
    const stockContainer = this.bundleStockContainer;
    if (!stockContainer) return;

    const variantId = stockContainer.dataset.variantId;
    const labels = bundleStockLabels(stockContainer.dataset);

    if (!variantId || variantId === 'null' || variantId === 'undefined') {
      stockContainer.innerHTML = unavailableMarkup(labels);
      return;
    }

    try {
      const data = await requestBundleStock(variantId);
      // The picker may have moved while the request was in flight: only the
      // answer for the variant currently shown is allowed to paint.
      if (stockContainer.dataset.variantId !== variantId) return;

      stockContainer.innerHTML = renderBundleStockMarkup(data, {
        labels,
        showQuantity: stockContainer.dataset.showQuantity !== 'false',
        locationNameOverrides: stockContainer.dataset.locationNameOverrides || '',
      });
    } catch (e) {
      console.error('[custom-product-card] Error loading bundle stock:', e);
      if (stockContainer.dataset.variantId === variantId) {
        stockContainer.innerHTML = unavailableMarkup(labels);
      }
    }
  }

  /**
   * Handles variant update
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  handleVariantUpdate(event) {
    const variantData = event.detail.resource;
    const priceContainer = this.querySelector('.product-card__price');
    const pricesScript = this.querySelector('script[data-variant-prices]');

    if (priceContainer && variantData && pricesScript) {
      try {
        const pricesData = JSON.parse(pricesScript.textContent);
        const variantPricing = pricesData[variantData.id];

        if (variantPricing) {
          const priceSpan = priceContainer.querySelector('span.price');
          if (priceSpan) priceSpan.innerHTML = variantPricing.price;

          let compareSpan = priceContainer.querySelector('.compare-at-price');
          if (variantPricing.compare_at_price) {
            if (compareSpan) {
              compareSpan.innerHTML = variantPricing.compare_at_price;
            } else {
              compareSpan = document.createElement('span');
              compareSpan.className = 'compare-at-price';
              compareSpan.innerHTML = variantPricing.compare_at_price;
              priceContainer.appendChild(compareSpan);
            }
          } else if (compareSpan) {
            compareSpan.remove();
          }
        }
      } catch (e) {
        console.error('[custom-product-card] Error updating price:', e);
      }
    }

    // Per-location stock is a variant metafield, so the list has to follow the
    // picker. The map holds markup already rendered by the same Liquid snippet
    // as the server-side pass (location-stock-list.liquid), which is why there
    // is no second copy of that markup in this file. Absent map, absent
    // container or unknown variant: leave what is on screen alone rather than
    // blanking it, the same way the price block above bails out.
    //
    // innerHTML is safe here because the only interpolated value in that markup
    // is the location label, which location-stock-list.liquid runs through
    // `| escape`. That is also what keeps a location name from carrying a
    // literal `</script>` out of the JSON block and closing the tag early.
    const stockContainer = this.querySelector('.product-card__location-stock');
    const stockScript = this.querySelector('script[data-variant-location-stock]');

    if (stockContainer && stockScript && variantData) {
      try {
        const stockMap = JSON.parse(stockScript.textContent);
        const stockMarkup = stockMap[variantData.id];

        if (typeof stockMarkup === 'string') stockContainer.innerHTML = stockMarkup;
      } catch (e) {
        console.error('[custom-product-card] Error updating location stock:', e);
      }
    }

    // A bundle has no markup map: its stock is fetched, so the picker moving
    // means a request for the new variant (served from the page cache when
    // that variant was already asked for).
    const bundleStockContainer = this.bundleStockContainer;
    if (bundleStockContainer && variantData?.id) {
      bundleStockContainer.dataset.variantId = String(variantData.id);
      this.loadBundleStock();
    }

    // The "Add to Cart" and "Request this item" buttons are inherently managed
    // by the native product-form.js logic. No manual DOM morphing needed here.

    // Point the product page links at the selected variant. The URL is built
    // from the variant id, not read from the fetched HTML: that HTML is a whole
    // product page whose first <a> is the "skip to content" link (#MainContent),
    // which turned the card links into an anchor on the collection page.
    if (variantData?.id) {
      /** @type {NodeListOf<HTMLAnchorElement>} */
      const links = this.querySelectorAll('a.product-card__image-link, h3.product-card__title a');
      links.forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set('variant', String(variantData.id));
        link.setAttribute('href', `${url.pathname}${url.search}`);
      });
    }

    // Update internal state of the variant picker if the event originated from outside
    /** @type {any} */
    const variantPicker = this.querySelector('swatches-variant-picker-component');
    if (event.target !== variantPicker && variantPicker) {
      variantPicker.updateVariantPicker(event.detail.data.html);
    }
  }
}

if (!customElements.get('custom-product-card')) {
  customElements.define('custom-product-card', CustomProductCard);
}
